import { api } from "@glasshalf/api-client";
import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConfirmImpactDialog } from "../../components/ConfirmImpactDialog";
import { DataTable } from "../../components/DataTable";
import { EmptyState } from "../../components/EmptyState";
import { SavedViews, type SavedView } from "../../components/SavedViews";
import { StatusBadge } from "../../components/StatusBadge";
import { formatNumber, normalizeText } from "../../lib/format";
import { fulfillmentItems } from "../../lib/selectors";
import type { AnyRecord, AppData, DrawerState } from "../../lib/types";

type ShippingPageProps = {
  data: AppData;
  drawer: DrawerState;
  setDrawer: (drawer: DrawerState) => void;
};

type FulfillmentStep = "pick" | "pack" | "ship";

export function ShippingPage({ data, drawer, setDrawer }: ShippingPageProps) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [pendingItem, setPendingItem] = useState<AnyRecord | null>(null);
  const [pendingStep, setPendingStep] = useState<FulfillmentStep | null>(null);
  const [reason, setReason] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const items = fulfillmentItems(data);
  const activeView = params.get("view") || "open";
  const views: SavedView[] = [
    { key: "open", label: "출고 대기", count: items.filter((item) => item.status !== "shipped").length },
    { key: "stock_check", label: "재고 확인", count: items.filter((item) => item.status === "stock_check").length },
    { key: "ready", label: "피킹 대기", count: items.filter((item) => item.canPick).length },
    { key: "picked", label: "패킹 대기", count: items.filter((item) => item.status === "picked").length },
    { key: "packed", label: "출고 확정", count: items.filter((item) => item.status === "packed").length },
    { key: "all", label: "전체", count: items.length }
  ];
  const filtered = items.filter((item) => {
    const text = normalizeText([item.orderIdMasked, item.orderKey, item.productName, item.sku, item.readableAlias, item.statusLabel].join(" "));
    if (query && !text.includes(normalizeText(query))) return false;
    if (activeView === "open") return item.status !== "shipped";
    if (activeView === "ready") return Boolean(item.canPick);
    if (activeView === "all") return true;
    return item.status === activeView;
  });
  const mutation = useMutation({
    mutationFn: () => {
      if (!pendingItem || !pendingStep) throw new Error("출고 라인을 선택해 주세요.");
      return api.fulfillmentStep(pendingStep, {
        channelId: Number(pendingItem.channelId || 0),
        orderKey: String(pendingItem.orderKey || ""),
        lineKey: String(pendingItem.lineKey || ""),
        reason,
        trackingNo: pendingStep === "ship" ? trackingNo : undefined
      });
    },
    onSuccess: async () => {
      setPendingItem(null);
      setPendingStep(null);
      setReason("");
      setTrackingNo("");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });
  const selectedId = drawer?.type === "shipping" ? shippingRowId(drawer.record) : "";
  const columns = useMemo<ColumnDef<AnyRecord>[]>(() => [
    {
      accessorKey: "orderIdMasked",
      header: "주문",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.orderIdMasked || row.original.orderKey || "-"}</strong>
          <span>{row.original.channelName || row.original.channelCode || "온라인몰"}</span>
        </div>
      ),
      size: 210
    },
    {
      accessorKey: "productName",
      header: "상품/SKU",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.productName || "-"}</strong>
          <span>{row.original.readableAlias || row.original.sku || "-"}</span>
        </div>
      ),
      size: 280
    },
    { accessorKey: "locationName", header: "위치", cell: ({ row }) => row.original.locationName || row.original.locationCode || "-" },
    { accessorKey: "quantity", header: "주문수량", cell: ({ row }) => <span className="num">{formatNumber(row.original.quantity || 0)}</span> },
    { accessorKey: "locationQuantity", header: "위치재고", cell: ({ row }) => <span className="num">{formatNumber(row.original.locationQuantity || 0)}</span> },
    { accessorKey: "statusLabel", header: "상태", cell: ({ row }) => <StatusBadge tone={shippingTone(row.original.status)}>{row.original.statusLabel || "확인"}</StatusBadge> },
    {
      id: "action",
      header: "작업",
      cell: ({ row }) => <ShippingAction item={row.original} onAction={(step) => {
        setPendingItem(row.original);
        setPendingStep(step);
      }} />
    }
  ], []);

  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <SavedViews views={views} active={activeView} onChange={(view) => setParams(view === "open" ? {} : { view })} />
        <div className="toolbar-row">
          <label className="search-field">
            검색
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="주문번호, 상품, SKU, 상태" />
          </label>
          <div className="meta-strip">
            <span>출고 대상</span>
            <strong>{formatNumber(items.filter((item) => item.status !== "shipped").length)}라인</strong>
          </div>
        </div>
      </section>
      <section className="panel">
        {items.length ? (
          <DataTable
            columns={columns}
            data={filtered}
            getRowId={shippingRowId}
            selectedId={selectedId}
            onSelect={(record) => setDrawer({ type: "shipping", record })}
            emptyTitle="필터에 맞는 출고 라인이 없습니다."
          />
        ) : (
          <EmptyState title="출고 대기 중인 주문예약이 없습니다." body="Cafe24 주문이 예약되면 피킹/패킹/출고 확정 순서로 표시됩니다." />
        )}
      </section>
      <ConfirmImpactDialog
        open={Boolean(pendingItem && pendingStep)}
        title={`${stepLabel(pendingStep)} 확인`}
        currentValue={currentImpact(pendingItem)}
        nextValue={nextImpact(pendingItem, pendingStep)}
        externalImpact={pendingStep === "ship" ? "백오피스 위치 재고를 차감합니다. Cafe24 배송 상태 쓰기는 아직 실행하지 않습니다." : "외부 채널에 즉시 쓰지 않고 백오피스 출고 상태만 변경합니다."}
        auditTrail="주문, SKU, 위치, 수량, 처리 단계가 감사 로그에 남습니다."
        reason={reason}
        confirmLabel={stepLabel(pendingStep)}
        busy={mutation.isPending}
        canConfirm={pendingStep !== "ship" || Boolean(trackingNo.trim())}
        extraFields={pendingStep === "ship" ? (
          <label>
            송장번호
            <input value={trackingNo} onChange={(event) => setTrackingNo(event.target.value)} placeholder="택배사 송장번호" />
          </label>
        ) : null}
        onReasonChange={setReason}
        onCancel={() => {
          setPendingItem(null);
          setPendingStep(null);
          setTrackingNo("");
        }}
        onConfirm={() => mutation.mutate()}
      />
    </div>
  );
}

function ShippingAction({ item, onAction }: { item: AnyRecord; onAction: (step: FulfillmentStep) => void }) {
  if (item.canPick) return <button type="button" onClick={(event) => { event.stopPropagation(); onAction("pick"); }}>피킹 확정</button>;
  if (item.canPack) return <button type="button" onClick={(event) => { event.stopPropagation(); onAction("pack"); }}>패킹 확정</button>;
  if (item.canShip) return <button type="button" className="primary" onClick={(event) => { event.stopPropagation(); onAction("ship"); }}>출고 확정</button>;
  return <span className="muted">{item.nextAction || "확인"}</span>;
}

function shippingRowId(row: AnyRecord): string {
  return `${row.channelId || ""}:${row.orderKey || ""}:${row.lineKey || ""}`;
}

function shippingTone(status?: string): "default" | "success" | "warning" | "danger" | "info" {
  if (status === "stock_check") return "warning";
  if (status === "shipped") return "success";
  if (status === "packed") return "info";
  return "default";
}

function stepLabel(step: FulfillmentStep | null): string {
  if (step === "pick") return "피킹 확정";
  if (step === "pack") return "패킹 확정";
  if (step === "ship") return "출고 확정";
  return "작업";
}

function currentImpact(item: AnyRecord | null): string {
  if (!item) return "-";
  return `${item.locationName || item.locationCode || "-"} 재고 ${formatNumber(item.locationQuantity || 0)}개 / 주문 ${formatNumber(item.quantity || 0)}개`;
}

function nextImpact(item: AnyRecord | null, step: FulfillmentStep | null): string {
  if (!item) return "-";
  if (step === "ship") return `${item.locationName || item.locationCode || "-"} 재고 ${formatNumber(Number(item.locationQuantity || 0) - Number(item.quantity || 0))}개`;
  return `${stepLabel(step)} 상태로 변경`;
}
